import React, { createContext, useContext, useState } from 'react';

const THEME_STORAGE_KEY = 'walking_nutrition_theme';

// Preset themes - "Modern Organic Tech" design system
export const THEMES = {
  default: {
    id: 'default',
    name: 'Vitality',
    description: 'Modern Organic Tech',
    gradient: 'from-slate-950 via-slate-900 to-slate-950',
    headerBg: 'bg-slate-950',
    cardBg: 'bg-white/5 backdrop-blur-md',
    cardBgSolid: 'bg-slate-900',
    accent: 'text-blue-400',
    accentBg: 'bg-blue-500',
    accentHover: 'hover:bg-blue-600',
    secondary: 'text-amber-400',
    secondaryBg: 'bg-amber-500',
    border: 'border-slate-800',
    borderLight: 'border-white/10',
    primary: 'blue',
    // Button styles
    btnPrimary: 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40',
    btnSecondary: 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700',
    // Card styles  
    cardDashboard: 'bg-slate-900/80 border border-slate-800 rounded-3xl backdrop-blur-md',
    cardStat: 'bg-slate-800/50 rounded-2xl',
    // Text styles
    textMuted: 'text-slate-400',
    textPrimary: 'text-white',
  },
  ocean: {
    id: 'ocean',
    name: 'Deep Ocean',
    description: 'Calm blue depths',
    gradient: 'from-slate-950 via-blue-950 to-slate-950',
    headerBg: 'bg-blue-950',
    cardBg: 'bg-white/5 backdrop-blur-md',
    cardBgSolid: 'bg-blue-950',
    accent: 'text-cyan-400',
    accentBg: 'bg-cyan-500',
    accentHover: 'hover:bg-cyan-600',
    secondary: 'text-blue-400',
    secondaryBg: 'bg-blue-500',
    border: 'border-blue-900',
    borderLight: 'border-white/10',
    primary: 'cyan',
    btnPrimary: 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg shadow-cyan-500/20',
    btnSecondary: 'bg-blue-900 hover:bg-blue-800 text-white border border-blue-800',
    cardDashboard: 'bg-blue-950/80 border border-blue-900 rounded-3xl backdrop-blur-md',
    cardStat: 'bg-blue-900/50 rounded-2xl',
    textMuted: 'text-blue-300',
    textPrimary: 'text-white',
  },
  forest: {
    id: 'forest',
    name: 'Forest',
    description: 'Natural green vibes',
    gradient: 'from-slate-950 via-blue-950 to-slate-950',
    headerBg: 'bg-blue-950',
    cardBg: 'bg-white/5 backdrop-blur-md',
    cardBgSolid: 'bg-blue-950',
    accent: 'text-lime-400',
    accentBg: 'bg-lime-500',
    accentHover: 'hover:bg-lime-600',
    secondary: 'text-blue-400',
    secondaryBg: 'bg-blue-500',
    border: 'border-blue-900',
    borderLight: 'border-white/10',
    primary: 'lime',
    btnPrimary: 'bg-lime-500 hover:bg-lime-600 text-slate-900 shadow-lg shadow-lime-500/20',
    btnSecondary: 'bg-blue-900 hover:bg-blue-800 text-white border border-blue-800',
    cardDashboard: 'bg-blue-950/80 border border-blue-900 rounded-3xl backdrop-blur-md',
    cardStat: 'bg-blue-900/50 rounded-2xl',
    textMuted: 'text-blue-300',
    textPrimary: 'text-white',
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm orange glow',
    gradient: 'from-slate-950 via-orange-950 to-slate-950',
    headerBg: 'bg-orange-950',
    cardBg: 'bg-white/5 backdrop-blur-md',
    cardBgSolid: 'bg-orange-950',
    accent: 'text-amber-400',
    accentBg: 'bg-amber-500',
    accentHover: 'hover:bg-amber-600',
    secondary: 'text-orange-400',
    secondaryBg: 'bg-orange-500',
    border: 'border-orange-900',
    borderLight: 'border-white/10',
    primary: 'amber',
    btnPrimary: 'bg-amber-500 hover:bg-amber-600 text-slate-900 shadow-lg shadow-amber-500/20',
    btnSecondary: 'bg-orange-900 hover:bg-orange-800 text-white border border-orange-800',
    cardDashboard: 'bg-orange-950/80 border border-orange-900 rounded-3xl backdrop-blur-md',
    cardStat: 'bg-orange-900/50 rounded-2xl',
    textMuted: 'text-orange-300',
    textPrimary: 'text-white',
  },
  purple: {
    id: 'purple',
    name: 'Purple Night',
    description: 'Mystical purple',
    gradient: 'from-slate-950 via-purple-950 to-slate-950',
    headerBg: 'bg-purple-950',
    cardBg: 'bg-white/5 backdrop-blur-md',
    cardBgSolid: 'bg-purple-950',
    accent: 'text-violet-400',
    accentBg: 'bg-violet-500',
    accentHover: 'hover:bg-violet-600',
    secondary: 'text-pink-400',
    secondaryBg: 'bg-pink-500',
    border: 'border-purple-900',
    borderLight: 'border-white/10',
    primary: 'violet',
    btnPrimary: 'bg-violet-500 hover:bg-violet-600 text-white shadow-lg shadow-violet-500/20',
    btnSecondary: 'bg-purple-900 hover:bg-purple-800 text-white border border-purple-800',
    cardDashboard: 'bg-purple-950/80 border border-purple-900 rounded-3xl backdrop-blur-md',
    cardStat: 'bg-purple-900/50 rounded-2xl',
    textMuted: 'text-purple-300',
    textPrimary: 'text-white',
  },
};

const ThemeContext = createContext({
  theme: THEMES.default,
  setTheme: () => {},
  themeId: 'default',
});

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return (saved && THEMES[saved]) ? saved : 'default';
  });

  const setTheme = (id) => {
    if (THEMES[id]) {
      setThemeId(id);
      localStorage.setItem(THEME_STORAGE_KEY, id);
    }
  };

  const theme = THEMES[themeId] || THEMES.default;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themeId }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeProvider;
